<template>
  <nav
    class="client-side-nav"
    data-pendo-ds="client_side_nav"
  >
    <div class="side-nav-container cr-flex cr-flex-col cr-justify-between cr-w-100 cr-h-100">
      <div class="top-nav">
        <ClientSideNavMenuItem
          v-if="displayHomepageLink"
          :active="activeMenuKey === 'client-home'"
          icon="fa-home"
          label="Home"
          link="client-home"
          use-router-link
        />
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-marketplace'"
          icon="fa-store"
          label="Marketplace"
          link="client-marketplace"
          use-router-link
        />
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-my-pantry'"
          :disabled="disableMyPantryLink"
          icon="fa-apple-whole"
          label="My Pantry"
          link="client-my-pantry"
          :permissible="!disableMyPantryLink"
          :show-tooltip="disableMyPantryLink"
          :tooltip-text="myPantryTooltipText"
          use-router-link
        />
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-orders-index'"
          :disabled="loggedOutUser"
          icon="fa-truck"
          label="Orders"
          link="client-orders-index"
          :permissible="!loggedOutUser"
          :show-tooltip="loggedOutUser"
          :tooltip-text="loggedOutTooltipText"
          use-router-link
        />
        <ClientSideNavMenuItem
          v-if="$screenSize.value >= 768"
          :active="activeMenuKey === 'client-reports'"
          :disabled="loggedOutUser"
          icon="fa-chart-bar"
          label="Reports"
          link="client-reports"
          :permissible="!loggedOutUser"
          :show-tooltip="loggedOutUser"
          :tooltip-text="loggedOutTooltipText"
          use-router-link
        />
        <ClientSideNavMenuItem
          v-else
          :disabled="loggedOutUser"
          expandable
          :expanded="expandedId === 'reports'"
          icon="fa-chart-bar"
          label="Reports"
          :permissible="!loggedOutUser"
          :show-tooltip="loggedOutUser"
          :tooltip-text="loggedOutTooltipText"
          @expanded="handleExpansion('reports')"
        />
        <TransitionExpand>
          <template v-if="expandedId === 'reports'">
            <div class="sub-menu-items">
              <ClientSideNavMenuItem
                :active="activeMenuKey === 'client-total-spend-report'"
                label="Total Spend"
                link="client-total-spend-report"
                :route-params="{ meta: { title: 'Total Spend Report' } }"
                use-router-link
              />
              <ClientSideNavMenuItem
                :active="activeMenuKey === 'client-product-performance-report'"
                :disabled="noLocationsWithActiveIcs"
                label="Product Performance"
                link="client-product-performance-report"
                :route-params="{ meta: { title: 'Product Performance' } }"
                :show-tooltip="noLocationsWithActiveIcs"
                :tooltip-text="noLocationsWithActiveIcs ? 'No locations have an active Inventory Center' : ''"
                use-router-link
              />
              <ClientSideNavMenuItem
                :active="activeMenuKey === 'client-product-spend-report'"
                label="Product Spend"
                link="client-product-spend-report"
                :route-params="{ meta: { title: 'Product Spend Report' } }"
                use-router-link
              />
              <ClientSideNavMenuItem
                :active="activeMenuKey === 'client-account-spend-report'"
                label="Account Spend"
                link="client-account-spend-report"
                :route-params="{ meta: { title: 'Account Spend' } }"
                use-router-link
              />
              <ClientSideNavMenuItem
                :active="activeMenuKey === 'client-category-spend-report'"
                label="Category Spend"
                link="client-category-spend-report"
                :route-params="{ meta: { title: 'Category Spend Report' } }"
                use-router-link
              />
              <ClientSideNavMenuItem
                :active="activeMenuKey === 'client-subcategory-spend-report'"
                label="Subcategory Spend"
                link="client-subcategory-spend-report"
                :route-params="{ meta: { title: 'Subcategory Spend Report' } }"
                use-router-link
              />
            </div>
          </template>
        </TransitionExpand>
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-budgets'"
          :disabled="disableBudgetsLink"
          icon="fa-circle-dollar"
          label="Budgets"
          link="client-budgets"
          :permissible="!disableBudgetsLink"
          :show-tooltip="disableBudgetsLink"
          :tooltip-text="budgetsTooltipText"
          use-router-link
        />
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-billing'"
          :disabled="disableBillingLink"
          icon="fa-file-invoice"
          label="Billing"
          link="client-billing"
          :permissible="!disableBillingLink"
          :show-tooltip="disableBillingLink"
          :tooltip-text="billingTooltipText"
          use-router-link
        />
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-operations-overview'"
          :disabled="disableOperationsLink"
          icon="fa-clipboard-check"
          label="Operations"
          link="client-operations-overview"
          :permissible="!disableOperationsLink"
          :show-tooltip="disableOperationsLink"
          :tooltip-text="operationsTooltipText"
          use-router-link
        />
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-schedule'"
          :disabled="loggedOutUser"
          icon="fa-calendar"
          label="Schedule"
          link="client-schedule"
          :permissible="!loggedOutUser"
          :show-tooltip="loggedOutUser"
          :tooltip-text="loggedOutTooltipText"
          use-router-link
        />
      </div>
      <div 
        v-if="!loggedOutUser"
        class="bottom-nav"
      >
        <ClientSideNavMenuItem
          :active="activeMenuKey === 'client-support'"
          icon="fa-circle-question"
          label="Support"
          link="client-support"
          :route-params="{ meta: { allowDemoUser: true, title: 'Support' } }"
          use-router-link
        />
        <ClientSideNavMenuItem
          v-if="!isDemoUser"
          :active="activeMenuKey === 'client-settings'"
          icon="fa-gear"
          label="Settings"
          link="client-settings"
          :route-params="{ meta: { allowDemoUser: true, title: 'Settings' } }"
          use-router-link
        />
        <ClientSideNavMenuItem
          v-if="!isDemoUser"
          icon="fa-sign-out-alt"
          label="Logout"
          :link="adminViewing ? '/admin/external_users/back_to_admin' : '/logout'"
        />
      </div>
      <div 
        v-else
        class="bottom-nav"
      >
        <ClientSideNavMenuItem
          icon="fa-tag"
          label="Pricing"
          link="https://info.craftydelivers.com/pricing"
        />
        <ClientSideNavMenuItem
          icon="fa-sign-in-alt"
          label="Sign In"
          link="/login"
        />
      </div>
    </div>
  </nav>
</template>

<script>
import ClientSideNavMenuItem from './ClientSideNavMenuItem.vue'
import TransitionExpand from './TransitionExpand.vue'

export default {
  name: 'ClientSideNav',
  components: {
    ClientSideNavMenuItem,
    TransitionExpand,
  },
  inject: [
    'adminViewing',
    'aiHomePageEnabled',
    'hasActiveDashboardICs',
    'companyUnderContract',
    'isDemoUser',
    'noLocationsWithActiveIcs',
    'userCanViewBudgets',
    'userHasBillingProfiles',
  ],
  props: {
    loggedOutUser: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      expandedId: '',
      linkSelected: false,
    }
  },
  computed: {
    loggedOutTooltipText() {
      return 'You must be logged in to view this feature.'
    },
    myPantryTooltipText() {
      if (this.loggedOutUser) {
        return this.loggedOutTooltipText
      } else if (!this.companyUnderContract) {
        return "You do not have permission to access this feature."
      } else if (!this.hasActiveDashboardICs) {
        return "You are not currently attached to any inventory centers. Please reach out if there is a specific inventory center you are looking to see."
      } else {
        return ''
      }
    },
    budgetsTooltipText() {
      if (this.loggedOutUser) {
        return this.loggedOutTooltipText
      } else if (!this.companyUnderContract) {
        return 'You do not have permission to access this feature.'
      } else if (!this.userCanViewBudgets) {
        return 'You are not currently attached to any budgets. Please reach out if there is a specific budget you are looking to see.'
      } else {
        return ''
      }
    },
    billingTooltipText() {
      if (this.loggedOutUser) {
        return this.loggedOutTooltipText
      } else if (!this.userHasBillingProfiles) {
        return 'You are not currently attached to any payment profiles. Please reach out if there is a specific payment profile you are looking to see.'
      } else {
        return ''
      }
    },
    operationsTooltipText() {
      if (this.loggedOutUser) {
        return this.loggedOutTooltipText
      } else if (!this.companyUnderContract) {
        return 'You do not have permission to access this feature.'
      } else {
        return ''
      }
    },
    activeMenuKey() {
      const routeName = this.$route.name

      if (['client-budgets-spend-v-budgeted', 'client-budgets-spend-and-fees', 'client-budgets-comparisons'].includes(routeName)) {
        return 'client-budgets'
      }

      if (routeName === 'client-order') {
        return 'client-orders-index'
      }

      if (['client-marketplace-checkout', 'client-product-details'].includes(routeName)) {
        return 'client-marketplace'
      }

      const reportRouteNames = [
        'client-product-performance-report',
        'client-total-spend-report',
        'client-product-spend-report',
        'client-account-spend-report',
        'client-category-spend-report',
        'client-subcategory-spend-report',
      ]

      if (this.$screenSize.value > 768 && reportRouteNames.includes(routeName)) {
        return 'client-reports'
      }

      if (['weekly-schedule', 'service-disruptions'].includes(routeName)) {
        return 'client-schedule'
      }

      return routeName
    },
    displayHomepageLink() {
      return !this.loggedOutUser && this.aiHomePageEnabled
    },
    disableMyPantryLink() {
      return this.loggedOutUser || !this.hasActiveDashboardICs || !this.companyUnderContract
    },
    disableBudgetsLink() {
      return this.loggedOutUser || !this.userCanViewBudgets || !this.companyUnderContract
    },
    disableBillingLink() {
      return this.loggedOutUser || !this.userHasBillingProfiles
    },
    disableOperationsLink() {
      return this.loggedOutUser || !this.companyUnderContract
    },
  },
  methods: {
    handleExpansion(id) {
      if (this.expandedId === id) {
        this.expandedId = ''
      } else {
        this.expandedId = id
      }
    },
  },
}
</script>

<style scoped lang="scss">
.client-side-nav {
  background-color: var(--salt);
  height: 100%;
  padding: 87px var(--space-4) var(--space-4) var(--space-4);
}
.sub-menu-items {
  max-width: 100%;
}
.bottom-nav {
  padding-bottom: 25px;
}

@media (min-width: 769px) {
  .client-side-nav {
    padding: 0;
    margin-top: 0;
  }
  .bottom-nav {
    padding-bottom: 0;
  }
}
</style>
